/**
 * ArTable cell clipboard serialization.
 *
 * Pure (no React/Valtio) helpers that move ArTable cell values of any column
 * type to and from the system clipboard. Consumed by `useArTableClipboard`
 * (native copy/paste events), `ArRowContextMenu` (async clipboard API) and
 * `FormContextMenu`, which calls `buildEnvelope` + `serializeArCells` directly
 * so a form field's copy is byte-identical to a single table cell's (SYD-2232).
 *
 * Transport: `text/plain` carries display labels (round-trips to Excel);
 * `text/html` carries a `<td data-ar-cell="<json>">` marker per cell so an
 * internal copy round-trips the exact value verbatim. `resolveColumn` maps a
 * schema column to a clipboard target: reference columns keep the entity
 * paste rules, field columns coerce per `fieldDataTypeId`, and formula /
 * protected / non-editable columns stay copyable but not pasteable.
 *
 * Formula invariant (SYD-2292): `buildCopyPayload` overlays freshly computed
 * results onto the row's cells, so a formula cell copies what the grid shows
 * rather than the persisted cache the server trigger owns. The overlay is
 * read-only — nothing here writes it back.
 *
 * Row transport (SYD-2314): `buildRowCopyPayload` widens the same markers into
 * a `<table data-ar-rows>` holding one `<tr>` per row and one `<td>` per column.
 * Every envelope carries its source column's `typeId`, which is what a paste
 * matches a destination column on — never the column's position.
 *
 * Derive invariant (SYD-2404): when a paste met cells a Form Derive projection
 * owns, `pasteSummary` reports it in the copy deck's own words rather than
 * folding them into the generic skip count. A silent partial success is the
 * failure the deck exists to prevent.
 */

import { formatCellValue, convertTimestampToDate, isRangeValue } from '@/components/exportTable/exportTableToExcel';
import { formDeriveCopy } from '@/services/formDerives';
import { UIElementType } from '#types/System.types';
import type { FieldType } from '#types/System.types';
import { applyArFormulaResultsToCells, computeArRowFormulas } from '#types';
import type { ArFormulaEngineConfig } from '#types';
import type { ArCellValue, ArRowData, ArRowId, ArTableType, TypeId } from '#types/System.Artifacts.types';
import type { TimeRange } from '#types/System.FieldTypes.types';

const AR_CELL_MARKER = 'data-ar-cell';
const AR_ROW_MARKER = 'data-ar-rows';

type ArCellClipboardEnvelope = {
  /** Source column identity — the field id, or the entity id for reference columns. Matches a destination column across tables. */
  typeId?: TypeId;
  /** Raw cell value, verbatim */
  value: ArCellValue;
  /** Source column kind — decides verbatim vs coerce on paste */
  columnKind: 'reference' | 'field';
  /** Source field data type (field columns) — same-type paste is verbatim */
  fieldDataTypeId?: string;
  /** Source column entityId (reference columns) */
  entityId?: string;
  /** Source column entityGroupId (reference columns) */
  entityGroupId?: string;
};

type ParsedClipboard =
  | { kind: 'structured'; envelopes: ArCellClipboardEnvelope[] }
  | { kind: 'structuredRows'; rows: ArCellClipboardEnvelope[][] }
  | { kind: 'external'; lines: string[] };

type ColumnClipboardTarget =
  | { kind: 'reference'; entityId: string; entityGroupId?: string; allowManual: boolean; pasteable: boolean }
  | { kind: 'field'; fieldType?: FieldType; pasteable: boolean };

type CopyPayloadArgs = {
  focused: { rowId: ArRowId; columnId: TypeId };
  selection: {
    selectedCount: number;
    selectedRowIds: readonly ArRowId[];
    selectedRows: Readonly<Record<string, boolean>>;
  };
  rendered: readonly ArRowId[];
  target: ColumnClipboardTarget;
  /** The whole row, not one cell: the formula engine reads every operand. */
  getRowData: (rowId: ArRowId) => ArRowData | undefined;
  /** Drives the formula overlay; an empty config copies stored values verbatim. */
  formulaConfig: ArFormulaEngineConfig;
};

type RowCopyPayloadArgs = {
  /** Rows to copy, already in rendered order. */
  rowIds: readonly ArRowId[];
  schemaData: ArTableType | null | undefined;
  fieldTypes: Record<string, FieldType>;
  /** The whole row, not one cell: the formula engine reads every operand. */
  getRowData: (rowId: ArRowId) => ArRowData | undefined;
  /** Drives the formula overlay; an empty config copies stored values verbatim. */
  formulaConfig: ArFormulaEngineConfig;
};

type PasteResultSummaryInput = {
  affected: number;
  skipped: number;
  created: number;
  /** Of `skipped`, how many were cells a Form Derive projection owns. */
  derived: number;
  /**
   * Whole ROWS the destination refused, which only a row paste produces. Its
   * `derived` is always `0` — the rows do not exist yet, so no projection can
   * claim their cells — so the two never appear in one sentence.
   */
  rejected?: number;
};

const escapeAttr = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/'/g, '&#39;');

const escapeText = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function cellClipboardLabel(value: ArCellValue, fieldType?: FieldType): string {
  if (value != null && typeof value === 'object' && isRangeValue(value)) {
    const range = value as TimeRange;
    const start = convertTimestampToDate(range.start);
    const end = convertTimestampToDate(range.end);
    if (start instanceof Date && end instanceof Date) {
      return `${start.toISOString()} - ${end.toISOString()}`;
    }
  }
  const formatted = formatCellValue(value, fieldType) as unknown;
  if (formatted == null) return '';
  if (formatted instanceof Date) return formatted.toISOString();
  if (typeof formatted === 'string') return formatted;
  return String(formatted);
}

function serializeArCells(
  envelopes: ArCellClipboardEnvelope[],
  fieldType?: FieldType
): { plain: string; html: string } {
  const plain = envelopes.map((e) => cellClipboardLabel(e.value, fieldType)).join('\n');
  const rows = envelopes
    .map(
      (e) =>
        `<tr><td ${AR_CELL_MARKER}="${escapeAttr(JSON.stringify(e))}">${escapeText(cellClipboardLabel(e.value, fieldType))}</td></tr>`
    )
    .join('');
  return { plain, html: `<table><tbody>${rows}</tbody></table>` };
}

function serializeArRowGrid(
  rows: readonly (readonly { envelope: ArCellClipboardEnvelope; label: string }[])[]
): { plain: string; html: string } {
  const plain = rows.map((cells) => cells.map((cell) => cell.label).join('\t')).join('\n');
  const body = rows
    .map(
      (cells) =>
        `<tr>${cells
          .map(
            (cell) =>
              `<td ${AR_CELL_MARKER}="${escapeAttr(JSON.stringify(cell.envelope))}">${escapeText(cell.label)}</td>`
          )
          .join('')}</tr>`
    )
    .join('');
  return { plain, html: `<table ${AR_ROW_MARKER}="1"><tbody>${body}</tbody></table>` };
}

function readArCellEnvelopes(scope: ParentNode): ArCellClipboardEnvelope[] {
  return Array.from(scope.querySelectorAll(`[${AR_CELL_MARKER}]`)).flatMap((node) => {
    const raw = node.getAttribute(AR_CELL_MARKER);
    if (raw == null) return [];
    try {
      return [JSON.parse(raw) as ArCellClipboardEnvelope];
    } catch {
      return [];
    }
  });
}

function parseClipboard(dt: { getData(type: string): string }): ParsedClipboard {
  const html = dt.getData('text/html');
  if (html && html.includes(AR_CELL_MARKER)) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const rowTable = doc.querySelector(`table[${AR_ROW_MARKER}]`);
    if (rowTable) {
      const rows = Array.from(rowTable.querySelectorAll('tr'))
        .map((tr) => readArCellEnvelopes(tr))
        .filter((envelopes) => envelopes.length > 0);
      return { kind: 'structuredRows', rows };
    }
    return { kind: 'structured', envelopes: readArCellEnvelopes(doc) };
  }

  const text = dt.getData('text/plain') ?? '';
  const lines = text.split(/\r\n|\r|\n/).map((line) => line.split('\t')[0]);
  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  return { kind: 'external', lines };
}

/**
 * Whether a column accepts pasted values: not hidden, not a formula output,
 * not protected, and not marked read-only (`editable === false`). Single source
 * of truth for both `resolveColumn` and `pasteDownColumn`.
 */
function isColumnPasteable(schemaData: ArTableType | null | undefined, columnId: TypeId): boolean {
  const column = (schemaData?.columns ?? []).find((c) => c.typeId === columnId);
  if (!column) return false;
  if (column.visibilityTypes?.includes('hidden') ?? false) return false;
  const formulaTypeIds = new Set(schemaData?.formulaSequence?.map((e) => e.typeId) ?? []);
  const protectedTypeIds = new Set(schemaData?.protected?.protectedTypeIds ?? []);
  if (formulaTypeIds.has(columnId) || protectedTypeIds.has(columnId)) return false;
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- `editable` is the live read-only-column flag (set by ArColumnPanel, honored by keyboard nav)
  return column.editable !== false;
}

function resolveColumn(
  schemaData: ArTableType | null | undefined,
  fieldTypes: Record<string, FieldType>,
  columnId: TypeId
): ColumnClipboardTarget | null {
  const column = (schemaData?.columns ?? []).find((c) => c.typeId === columnId);
  if (!column) return null;
  if (column.visibilityTypes?.includes('hidden') ?? false) return null;

  const pasteable = isColumnPasteable(schemaData, columnId);

  if (column.uiElementType === UIElementType.Reference) {
    if (column.referenceType !== 'entity' || !column.entityId) {
      return { kind: 'field', pasteable: false };
    }
    return {
      kind: 'reference',
      entityId: column.entityId,
      ...(column.entityGroupId ? { entityGroupId: column.entityGroupId } : null),
      allowManual: column.allowTypeIn !== false,
      pasteable,
    };
  }

  return { kind: 'field', fieldType: fieldTypes[columnId], pasteable };
}

function buildEnvelope(
  typeId: TypeId,
  value: ArCellValue,
  target: ColumnClipboardTarget
): ArCellClipboardEnvelope {
  if (target.kind === 'reference') {
    return {
      typeId,
      value,
      columnKind: 'reference',
      entityId: target.entityId,
      ...(target.entityGroupId ? { entityGroupId: target.entityGroupId } : null),
    };
  }
  return {
    typeId,
    value,
    columnKind: 'field',
    ...(target.fieldType?.fieldDataTypeId ? { fieldDataTypeId: target.fieldType.fieldDataTypeId } : null),
  };
}

function buildCopyPayload(args: CopyPayloadArgs): { plain: string; html: string } {
  const { focused, selection, rendered, target, getRowData, formulaConfig } = args;
  const anchorRowId = focused.rowId;

  const scopeRowIds = (() => {
    if (selection.selectedCount > 1 && (selection.selectedRows[anchorRowId] ?? false) === true) {
      const selectedSet = new Set(selection.selectedRowIds);
      const ordered = rendered.filter((id) => selectedSet.has(id));
      return ordered.length > 0 ? ordered : [anchorRowId];
    }
    return [anchorRowId];
  })();

  const envelopes: ArCellClipboardEnvelope[] = scopeRowIds.map((rowId) => {
    const data = getRowData(rowId);
    /**
     * The overlay puts a computed result exactly where a stored one sits — plain
     * targets in `value`, dotted targets in the composite sub-value — so a
     * skipped row (which the engine declines to compute) reads through unchanged.
     */
    const cells = applyArFormulaResultsToCells(
      data?.cells,
      computeArRowFormulas(rowId, data, formulaConfig),
    );
    const value: ArCellValue = cells[focused.columnId]?.value ?? null;
    return buildEnvelope(focused.columnId, value, target);
  });

  return serializeArCells(envelopes, target.kind === 'field' ? target.fieldType : undefined);
}

function buildRowCopyPayload(args: RowCopyPayloadArgs): { plain: string; html: string } {
  const { rowIds, schemaData, fieldTypes, getRowData, formulaConfig } = args;

  const columns = (schemaData?.columns ?? []).flatMap((column) => {
    const target = resolveColumn(schemaData, fieldTypes, column.typeId);
    return target ? [{ typeId: column.typeId, target }] : [];
  });

  const rows = rowIds.map((rowId) => {
    const data = getRowData(rowId);
    const cells = applyArFormulaResultsToCells(
      data?.cells,
      computeArRowFormulas(rowId, data, formulaConfig),
    );
    return columns.map(({ typeId, target }) => {
      const value: ArCellValue = cells[typeId]?.value ?? null;
      return {
        envelope: buildEnvelope(typeId, value, target),
        label: cellClipboardLabel(value, target.kind === 'field' ? target.fieldType : undefined),
      };
    });
  });

  return serializeArRowGrid(rows);
}

function pasteSummary(result: PasteResultSummaryInput): string | null {
  if (result.derived > 0) {
    const attempted = result.affected + result.skipped;
    const derivedSentence = formDeriveCopy.pastePartiallySkipped(
      result.affected,
      attempted,
      result.derived,
    );
    return result.created > 0
      ? `${derivedSentence} Added ${result.created} row${result.created === 1 ? '' : 's'}.`
      : derivedSentence;
  }

  const rejected = result.rejected ?? 0;
  const parts: string[] = [];
  if (result.affected > 0) parts.push(`Pasted ${result.affected}`);
  if (result.created > 0) parts.push(`added ${result.created} row${result.created === 1 ? '' : 's'}`);
  if (result.skipped > 0) parts.push(`skipped ${result.skipped}`);
  if (rejected > 0) parts.push(`${rejected} row${rejected === 1 ? '' : 's'} could not be added`);
  return parts.length > 0 ? parts.join(', ') : null;
}

export {
  AR_CELL_MARKER,
  AR_ROW_MARKER,
  cellClipboardLabel,
  serializeArCells,
  parseClipboard,
  isColumnPasteable,
  resolveColumn,
  buildEnvelope,
  buildCopyPayload,
  buildRowCopyPayload,
  pasteSummary,
};
export type {
  ArCellClipboardEnvelope,
  ParsedClipboard,
  ColumnClipboardTarget,
  CopyPayloadArgs,
  RowCopyPayloadArgs,
};
